import { useCallback, useEffect, useState } from 'react'
import type { IconType } from 'react-icons'
import { FiShield, FiLoader, FiRefreshCw, FiSlash, FiAlertTriangle, FiMonitor, FiKey, FiCopy, FiClock, FiMapPin, FiCheck } from 'react-icons/fi'

// Painel de Segurança (Tecnologia → Segurança).
//
// POR QUE ELE EXISTE: em 01/09/2026 perguntaram "aquele redirect estranho foi
// ataque ou fomos nós?" e a resposta foi "não dá pra saber, e não daria pra
// saber de nenhum dia". Esta tela é a resposta passar a existir.
//
// DOIS BLOCOS, e os números como CHIPS no título — não como cards. Doze cartões
// leem como bagunça; o que importa aqui é: alguém está batendo em porta
// fechada? quem está logado agora?

type Resumo = {
  horas: number; escritas: number; autores: number; negadas: number
  loginsFalhos: number; ipsComFalha: number; sessoesAtivas: number; sessoesSemIp: number
  trilhaDesde: string | null
}
type LinhaAudit = {
  id: number; username: string | null; is_admin: number; method: string; path: string
  status: number | null; ip: string | null; user_agent: string | null; duracao_ms: number | null
  detalhe: string | null; created_at: string
}
type LinhaLogin = {
  id: number; username: string | null; sucesso: number; motivo: string | null
  ip: string | null; user_agent: string | null; created_at: string; geo?: Geo | null
}
type PorIp = { ip: string; tentativas: number; usuarios: number; de: string; ate: string; geo: Geo | null }
type Geo = {
  interno?: boolean; desligado?: boolean; erro?: string | null
  pais?: string | null; pais_sigla?: string | null; regiao?: string | null; cidade?: string | null
  operadora?: string | null; movel?: number; hospedagem?: number; proxy?: number
}
type Sessao = {
  id: string; user_id: string; username: string | null; full_name: string | null; is_admin: number
  ip_address: string | null; user_agent: string | null; created_at: string; expires_at: string
  geo: Geo | null
}
type GrupoSessao = {
  user_id: string; username: string | null; full_name: string | null; is_admin: number
  total: number; ultima: string; sessoes: Sessao[]
}

type Motivo = { tipo: string; texto: string; desde?: string | null }
type Pessoa = {
  id: string; username: string; full_name: string | null; is_admin: number
  password_changed_at: string | null; password_forca: string | null
  dias_desde_troca: number | null; last_login: string | null
  must_change_requested_at: string | null
  peso: number; motivos: Motivo[]
}
type Detalhe = {
  senha: EventoSenha[]
  logins: { ip: string | null; user_agent: string | null; created_at: string; geo: Geo | null }[]
  locais: { rotulo: string | null; ip_exemplo: string | null; vezes: number; primeiro_em: string; ultimo_em: string }[]
  sessoes: { id: string; ip_address: string | null; user_agent: string | null; created_at: string; geo: Geo | null }[]
  horas: { hora: number; n: number }[]
  escritas: { method: string; path: string; status: number | null; created_at: string }[]
}
type EventoSenha = {
  tipo: string; por_username: string | null; ip: string | null
  local: string | null; forca: string | null; created_at: string
}

type Props = { authenticatedFetch: (url: string, init?: RequestInit) => Promise<Response> }

const quando = (iso: string) => new Date(iso).toLocaleString('pt-BR', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
})
// O navegador cabe em uma palavra; a string inteira do user-agent não cabe em
// lugar nenhum e não diz mais nada de útil numa lista.
const navegador = (ua: string | null) => {
  if (!ua) return null
  if (/curl|wget|python|node-fetch|postman/i.test(ua)) return ua.split('/')[0].slice(0, 14)
  if (/edg/i.test(ua)) return 'Edge'
  if (/chrome/i.test(ua)) return 'Chrome'
  if (/safari/i.test(ua)) return 'Safari'
  if (/firefox/i.test(ua)) return 'Firefox'
  return 'outro'
}
// De onde o IP veio, em uma linha. Bandeira por código de país: dois
// caracteres regionais unicode, sem depender de biblioteca nem de imagem.
const bandeira = (sigla?: string | null) =>
  sigla && sigla.length === 2
    ? String.fromCodePoint(...[...sigla.toUpperCase()].map(c => 0x1f1e6 + c.charCodeAt(0) - 65))
    : ''
const procedencia = (g?: Geo | null) => {
  if (!g) return null
  if (g.interno) return 'rede interna'
  if (g.desligado) return null
  if (g.erro) return null
  const onde = [g.cidade, g.pais].filter(Boolean).join(' · ')
  return onde ? `${bandeira(g.pais_sigla)} ${onde}`.trim() : null
}
// Sinais que merecem cor: fora do Brasil, datacenter (VPS de atacante costuma
// ser hospedagem) ou proxy/VPN declarado.
const suspeito = (g?: Geo | null) =>
  !!g && !g.interno && !g.erro && (!!g.hospedagem || !!g.proxy || (!!g.pais_sigla && g.pais_sigla !== 'BR'))

const desdeQuando = (iso?: string | null) => {
  if (!iso) return null
  const seg = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000))
  if (seg < 3600) return `há ${Math.max(1, Math.round(seg / 60))} min`
  if (seg < 172800) return `há ${Math.round(seg / 3600)} h`
  return `há ${Math.round(seg / 86400)} dias`
}

const semBarra = { scrollbarWidth: 'none' as const, msOverflowStyle: 'none' as const }

function Chip({ n, label, alerta, titulo }: { n: number; label: string; alerta?: boolean; titulo?: string }) {
  return (
    <span title={titulo}
      className={`neu-tag rounded-full px-2 py-0.5 text-[10.5px] font-semibold whitespace-nowrap ${
        alerta && n > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
      }`}>
      <span className="tabular-nums text-foreground">{n}</span> {label}
    </span>
  )
}

export function SegurancaPanel({ authenticatedFetch }: Props) {
  const [horas, setHoras] = useState(24)
  const [aba, setAba] = useState<'trilha' | 'logins' | 'pessoas'>('pessoas')
  const [pessoas, setPessoas] = useState<Pessoa[]>([])
  const [totalAtivos, setTotalAtivos] = useState(0)
  const [detalhes, setDetalhes] = useState<Record<string, Detalhe>>({})
  const [temporaria, setTemporaria] = useState<{ usuario: string; senha: string } | null>(null)
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [soNegadas, setSoNegadas] = useState(false)
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [trilha, setTrilha] = useState<LinhaAudit[]>([])
  const [logins, setLogins] = useState<LinhaLogin[]>([])
  const [porIp, setPorIp] = useState<PorIp[]>([])
  const [grupos, setGrupos] = useState<GrupoSessao[]>([])
  const [aberto, setAberto] = useState<string | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [derrubando, setDerrubando] = useState<string | null>(null)

  const carrega = useCallback(async () => {
    setErro(null)
    try {
      const q = `horas=${horas}`
      const [r1, r2, r3, r4, r5] = await Promise.all([
        authenticatedFetch(`/api/seguranca/resumo?${q}`),
        authenticatedFetch(`/api/seguranca/auditoria?${q}${soNegadas ? '&so=negadas' : ''}`),
        authenticatedFetch(`/api/seguranca/logins?${q}${soNegadas ? '&so=falhas' : ''}`),
        authenticatedFetch('/api/seguranca/sessoes'),
        authenticatedFetch('/api/seguranca/riscos'),
      ])
      const [d1, d2, d3, d4, d5] = await Promise.all([r1.json(), r2.json(), r3.json(), r4.json(), r5.json()])
      setPessoas(d5.linhas || []); setTotalAtivos(d5.totalAtivos || 0)
      if (!d1.success) throw new Error(d1.error || 'falha ao ler o resumo')
      setResumo(d1); setTrilha(d2.linhas || []); setLogins(d3.linhas || [])
      setPorIp(d3.porIp || []); setGrupos(d4.usuarios || [])
    } catch (e) { setErro((e as Error).message) }
    finally { setCarregando(false) }
  }, [authenticatedFetch, horas, soNegadas])

  useEffect(() => { carrega() }, [carrega])

  const resetaSenha = async (u: Pessoa) => {
    if (!confirm(`Gerar senha temporária para ${u.username}? Ela cai fora do sistema agora e terá que definir uma senha nova ao entrar.`)) return
    setOcupado(u.id); setErro(null)
    try {
      const r = await authenticatedFetch(`/api/seguranca/usuarios/${u.id}/reset-senha`, { method: 'POST' })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui resetar')
      // Aparece UMA vez: não fica guardada legível em lugar nenhum.
      setTemporaria({ usuario: d.usuario, senha: d.senhaTemporaria })
      await carrega()
    } catch (e) { setErro((e as Error).message) }
    finally { setOcupado(null) }
  }

  // O histórico só é buscado quando alguém abre a pessoa: são N consultas, e
  // ninguém precisa do histórico de 10 pessoas pra olhar o de uma.
  const abreHistorico = async (id: string) => {
    if (aberto === id) { setAberto(null); return }
    setAberto(id)
    if (detalhes[id]) return
    try {
      const r = await authenticatedFetch(`/api/seguranca/usuarios/${id}/detalhe`)
      const d = await r.json()
      if (d.success) setDetalhes(h => ({ ...h, [id]: d as Detalhe }))
    } catch { /* detalhe é detalhe: não pode quebrar a lista */ }
  }

  const exigeTroca = async (u: Pessoa) => {
    if (!confirm(`Obrigar ${u.username} a definir uma senha nova? Ela cai fora agora e entra com a senha atual só pra trocar.`)) return
    setOcupado(u.id); setErro(null)
    try {
      const r = await authenticatedFetch(`/api/seguranca/usuarios/${u.id}/exigir-troca`, { method: 'POST' })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui exigir a troca')
      setDetalhes(h => { const c = { ...h }; delete c[u.id]; return c })
      await carrega()
    } catch (e) { setErro((e as Error).message) }
    finally { setOcupado(null) }
  }

  const trocaGeral = async () => {
    // Duas confirmações de propósito: isto interrompe quem estiver atendendo.
    const previa = await authenticatedFetch('/api/seguranca/forcar-troca-geral', {
      method: 'POST', body: JSON.stringify({}),
    }).then(r => r.json()).catch(() => null)
    if (!previa?.aviso) { setErro('não consegui checar o efeito antes de agir'); return }
    if (!confirm(`${previa.aviso}\n\nConfirma?`)) return
    setOcupado('geral'); setErro(null)
    try {
      const r = await authenticatedFetch('/api/seguranca/forcar-troca-geral', {
        method: 'POST', body: JSON.stringify({ confirmo: true }),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'falhou')
      alert(`Pronto: ${d.usuarios} pessoa(s) vão definir senha nova no próximo login e ${d.sessoesDerrubadas} sessão(ões) caíram. Você continua conectado.`)
      await carrega()
    } catch (e) { setErro((e as Error).message) }
    finally { setOcupado(null) }
  }

  const derruba = async (alvo: { url: string; chave: string }) => {
    setDerrubando(alvo.chave)
    try {
      const r = await authenticatedFetch(alvo.url, { method: 'DELETE' })
      const d = await r.json()
      if (!d.success) throw new Error(d.error || 'não consegui derrubar')
      await carrega()
    } catch (e) { setErro((e as Error).message) }
    finally { setDerrubando(null) }
  }

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5">
      {/* Título com os números na MESMA linha. */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        <FiShield className="w-4 h-4 text-primary shrink-0" />
        <span className="text-[15px] font-semibold">Segurança</span>
        {resumo && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Chip n={resumo.escritas} label="escritas" titulo={`${resumo.autores} pessoa(s) diferentes escreveram nas últimas ${horas}h`} />
            <Chip n={resumo.negadas} label="negadas" alerta titulo="Escritas que tomaram 401/403 — alguém batendo em porta fechada" />
            <Chip n={resumo.loginsFalhos} label="logins falhos" alerta titulo={`De ${resumo.ipsComFalha} IP(s) diferentes`} />
            <Chip n={pessoas.filter(p => p.motivos.length === 0).length} label={`em dia de ${totalAtivos}`}
              titulo="Pessoas sem nenhuma pendência — elas continuam na lista, no fim" />
            <Chip n={resumo.sessoesAtivas} label="sessões" titulo={resumo.sessoesSemIp > 0 ? `${resumo.sessoesSemIp} sem IP: abertas antes de 01/09/2026, quando o login voltou a gravar de onde vem` : undefined} />
          </div>
        )}
        <div className="ml-auto flex items-center gap-1.5">
          {[24, 168, 720].map(h => (
            <button key={h} onClick={() => setHoras(h)}
              className={`px-2 py-1 rounded-lg text-[11px] font-medium transition-colors ${
                horas === h ? 'neu-relief bg-primary text-primary-foreground' : 'neu-seg-btn text-muted-foreground hover:text-foreground'
              }`}>
              {h === 24 ? '24h' : h === 168 ? '7 dias' : '30 dias'}
            </button>
          ))}
          <button onClick={carrega} disabled={carregando} title="Atualizar"
            className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 disabled:opacity-50 transition-colors">
            <FiRefreshCw className={`w-3.5 h-3.5 ${carregando ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sem isto, trocar de 24h pra 30 dias não muda nada na tela e parece
          botão quebrado — quando é a gravação que começou agora. */}
      {resumo?.trilhaDesde && (() => {
        const inicio = new Date(resumo.trilhaDesde)
        const horasDeTrilha = (Date.now() - inicio.getTime()) / 3600000
        if (horasDeTrilha >= horas) return null
        return (
          <div className="shrink-0 text-[10.5px] text-muted-foreground">
            A trilha começou em <b className="text-foreground">{quando(resumo.trilhaDesde)}</b> — não há
            registro anterior a isso, então períodos maiores mostram o mesmo.
          </div>
        )
      })()}

      {temporaria && (
        <div className="shrink-0 rounded-xl neu-card border border-primary/40 px-3 py-2.5">
          <div className="text-[11.5px] font-semibold">Senha temporária de {temporaria.usuario}</div>
          <div className="mt-1 flex items-center gap-2">
            <code className="text-[14px] font-bold tracking-wide select-all">{temporaria.senha}</code>
            <button onClick={() => navigator.clipboard?.writeText(temporaria.senha)}
              className="p-1 rounded hover:bg-muted/40 text-muted-foreground" title="Copiar">
              <FiCopy className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => setTemporaria(null)} className="ml-auto text-[10.5px] text-muted-foreground hover:text-foreground">
              já anotei
            </button>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground">
            Aparece uma vez só — não fica guardada em lugar nenhum. Passe para a pessoa por um
            canal em que você confie; ela vai ter que definir a dela ao entrar.
          </div>
        </div>
      )}

      {erro && (
        <div className="shrink-0 rounded-xl neu-card px-3 py-2 text-[11.5px] text-red-600 dark:text-red-400 flex items-center gap-2">
          <FiAlertTriangle className="w-3.5 h-3.5 shrink-0" /> {erro}
        </div>
      )}

      {/* Força bruta em curso: some quando não há. Faixa e não card — é um
          aviso, não uma métrica que se acompanha. */}
      {porIp.length > 0 && porIp[0].tentativas >= 5 && (
        <div className="shrink-0 rounded-xl neu-card border border-amber-500/40 px-3 py-2">
          <div className="text-[11.5px] font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
            <FiAlertTriangle className="w-3.5 h-3.5" /> Tentativas de login em série
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
            {porIp.slice(0, 4).map(p => (
              <span key={p.ip} className="text-[10.5px] text-muted-foreground">
                <b className="text-foreground tabular-nums">{p.ip}</b>
                {procedencia(p.geo) && <> ({procedencia(p.geo)}{p.geo?.hospedagem ? ', datacenter' : ''})</>}
                {' · '}{p.tentativas} falhas
                {p.usuarios > 1 && <> em <b className="text-foreground">{p.usuarios}</b> usuários</>}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-3">
        {/* ── Bloco 1: o que aconteceu ── */}
        <div className="min-h-0 flex flex-col rounded-2xl glass-panel neu-bar overflow-hidden">
          <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border">
            {(['pessoas', 'trilha', 'logins'] as const).map(a => (
              <button key={a} onClick={() => setAba(a)}
                className={`px-2.5 py-1 rounded-lg text-[11.5px] font-medium transition-colors ${
                  aba === a ? 'neu-relief bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                }`}>
                {a === 'trilha' ? 'Escritas' : a === 'logins' ? 'Logins' : 'Pessoas'}
              </button>
            ))}
            {aba === 'pessoas' ? (
              <button onClick={trocaGeral} disabled={ocupado === 'geral'}
                title="Derruba todo mundo e obriga cada um a definir senha nova no próximo login"
                className="ml-auto h-7 px-2.5 rounded-lg text-[11px] font-medium bg-amber-500/15 text-amber-700 dark:text-amber-400 hover:bg-amber-500/25 disabled:opacity-50 transition-colors flex items-center gap-1.5">
                {ocupado === 'geral' ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiKey className="w-3 h-3" />}
                Forçar troca de todos
              </button>
            ) : (
              <label className="ml-auto flex items-center gap-1.5 text-[10.5px] text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" checked={soNegadas} onChange={e => setSoNegadas(e.target.checked)} className="accent-amber-500" />
                só o que falhou
              </label>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto" style={semBarra}>
            {carregando ? (
              <div className="flex items-center justify-center py-10"><FiLoader className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : aba === 'trilha' ? (
              trilha.length === 0 ? <Vazio texto="Nenhuma escrita registrada no período." />
              : trilha.map(l => (
                <div key={l.id} className="px-3 py-1.5 border-b border-border/60 flex items-center gap-2 text-[11px]">
                  <span className={`shrink-0 w-11 text-[9.5px] font-bold ${
                    l.method === 'DELETE' ? 'text-red-500' : l.method === 'POST' ? 'text-emerald-600 dark:text-emerald-400' : 'text-primary'
                  }`}>{l.method}</span>
                  <span className="min-w-0 flex-1 truncate text-[10.5px]" title={l.detalhe ? `${l.detalhe} · ${l.path}` : l.path}>
                    {l.detalhe
                      ? <span className={l.status && l.status >= 400 ? 'text-amber-700 dark:text-amber-400' : ''}>{l.detalhe}</span>
                      : <span className="font-mono">{l.path}</span>}
                  </span>
                  <span className={`shrink-0 tabular-nums text-[10.5px] ${
                    l.status && l.status >= 400 ? 'text-amber-600 dark:text-amber-400 font-semibold' : 'text-muted-foreground'
                  }`}>{l.status ?? '—'}</span>
                  <span className="shrink-0 w-28 truncate text-muted-foreground" title={l.username || 'sem login'}>
                    {l.username || <span className="italic">sem login</span>}
                  </span>
                  <span className="shrink-0 w-24 truncate text-[10px] text-muted-foreground tabular-nums" title={l.user_agent || ''}>{l.ip || '—'}</span>
                  <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">{quando(l.created_at)}</span>
                </div>
              ))
            ) : aba === 'pessoas' ? (
              pessoas.length === 0
                ? <Vazio texto={`Ninguém em atenção — as ${totalAtivos} contas ativas estão em dia.`} />
                : pessoas.map(u => {
                const expandida = aberto === u.id
                return (
                <div key={u.id} className="border-b border-border/60">
                  <div className="px-3 py-2 flex items-start gap-2">
                    <button onClick={() => abreHistorico(u.id)} className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11.5px] font-medium truncate">{u.username}</span>
                        {!!u.is_admin && <span className="neu-tag rounded-full px-1.5 text-[9px] font-semibold text-primary">admin</span>}
                        {u.password_forca && (
                          <span className={`neu-tag rounded-full px-1.5 text-[9px] font-semibold ${
                            u.password_forca === 'forte' ? 'text-emerald-600 dark:text-emerald-400'
                            : u.password_forca === 'boa' ? 'text-muted-foreground'
                            : 'text-amber-600 dark:text-amber-400'}`}>
                            senha {u.password_forca}
                          </span>
                        )}
                      </div>
                      {/* Um motivo por linha: "3 pendências" não diz o que fazer.
                          Sem motivo nenhum, a linha diz o estado — linha muda faz
                          parecer que faltou carregar. */}
                      {u.motivos.length === 0 && (
                        <div className="text-[10px] leading-snug text-emerald-600 dark:text-emerald-400">
                          • em dia
                          {u.password_changed_at && <span className="text-muted-foreground"> · senha trocada {desdeQuando(u.password_changed_at)}</span>}
                        </div>
                      )}
                      {u.motivos.map((m, i) => (
                        <div key={i} className={`text-[10px] leading-snug ${
                          m.tipo === 'local_estranho' || m.tipo === 'pendente' || m.tipo === 'nunca'
                            ? 'text-amber-700 dark:text-amber-400' : 'text-muted-foreground'
                        }`}>
                          • {m.texto}
                          {m.desde && <span className="text-muted-foreground"> · {desdeQuando(m.desde)}</span>}
                        </div>
                      ))}
                    </button>
                    <button onClick={() => resetaSenha(u)} disabled={ocupado === u.id}
                      title="Gerar senha temporária e derrubar as sessões desta pessoa"
                      className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors">
                      {ocupado === u.id ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiKey className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {expandida && (() => {
                    const d = detalhes[u.id]
                    if (!d) return <div className="px-6 py-3"><FiLoader className="w-3.5 h-3.5 animate-spin text-muted-foreground" /></div>
                    return (
                      <div className="pl-6 pr-3 pb-3 space-y-2.5">
                        {/* Ações da pessoa. Ficam DENTRO do expandido: fora, viram
                            três ícones por linha e a lista deixa de ser legível. */}
                        <div className="flex flex-wrap gap-1.5 pt-1">
                          <AcaoBtn onClick={() => exigeTroca(u)} ocupado={ocupado === u.id}
                            icone={FiKey} texto="Exigir troca de senha" />
                          <AcaoBtn onClick={() => resetaSenha(u)} ocupado={ocupado === u.id}
                            icone={FiRefreshCw} texto="Resetar (senha temporária)" />
                          {d.sessoes.length > 0 && (
                            <AcaoBtn onClick={() => derruba({ url: `/api/seguranca/sessoes/usuario/${u.id}`, chave: u.id })}
                              ocupado={derrubando === u.id} icone={FiSlash}
                              texto={`Derrubar ${d.sessoes.length} sessão${d.sessoes.length > 1 ? 'ões' : ''}`} perigo />
                          )}
                        </div>

                        <Bloco titulo="Senha" icone={FiKey} vazio="nenhum evento — o histórico começou em 01/09/2026">
                          {d.senha.map((e, i) => (
                            <Linha key={i}
                              rotulo={e.tipo === 'trocou' ? 'trocou' : e.tipo === 'exigida' ? 'troca exigida' : 'reset pelo admin'}
                              cor={e.tipo === 'trocou' ? 'ok' : 'atencao'}
                              meio={e.tipo === 'trocou'
                                ? [e.local, e.forca ? `senha ${e.forca}` : null].filter(Boolean).join(' · ')
                                : e.por_username ? `por ${e.por_username}` : ''}
                              quando={e.created_at} />
                          ))}
                        </Bloco>

                        <Bloco titulo="Entradas" icone={FiClock} vazio="nenhuma entrada registrada desde 01/09/2026">
                          {d.logins.slice(0, 12).map((l, i) => (
                            <Linha key={i} rotulo={navegador(l.user_agent) || '—'}
                              cor={suspeito(l.geo) ? 'atencao' : 'neutro'}
                              meio={[l.ip, procedencia(l.geo)].filter(Boolean).join(' · ')}
                              quando={l.created_at} />
                          ))}
                        </Bloco>

                        <Bloco titulo="Lugares de onde entra" icone={FiMapPin} vazio="nenhum lugar registrado">
                          {d.locais.map((l, i) => (
                            <Linha key={i} rotulo={`${l.vezes}x`} cor="neutro"
                              meio={l.rotulo || l.ip_exemplo || '—'}
                              quando={l.ultimo_em} />
                          ))}
                        </Bloco>

                        {/* Faixa de horário: estranhar acesso às 3h sem ler 300
                            linhas de trilha. */}
                        {d.horas.length > 0 && (
                          <div>
                            <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                              Horário de trabalho (30 dias)
                            </div>
                            <div className="flex items-end gap-px h-8">
                              {Array.from({ length: 24 }, (_, h) => {
                                const achou = d.horas.find(x => Number(x.hora) === h)
                                const max = Math.max(...d.horas.map(x => Number(x.n)), 1)
                                const alt = achou ? Math.max(2, Math.round((Number(achou.n) / max) * 100)) : 0
                                const foraDoExpediente = h < 6 || h >= 22
                                return (
                                  <div key={h} className="flex-1 flex flex-col justify-end h-full"
                                    title={`${String(h).padStart(2, '0')}h — ${achou?.n || 0} ações`}>
                                    <div className={`w-full rounded-sm ${
                                      alt === 0 ? 'bg-muted/40' : foraDoExpediente ? 'bg-amber-500/70' : 'bg-primary/70'
                                    }`} style={{ height: `${Math.max(alt, 4)}%` }} />
                                  </div>
                                )
                              })}
                            </div>
                            <div className="flex justify-between text-[8.5px] text-muted-foreground mt-0.5">
                              <span>0h</span><span>6h</span><span>12h</span><span>18h</span><span>23h</span>
                            </div>
                          </div>
                        )}

                        <Bloco titulo="Últimas escritas" icone={FiCheck} vazio="nada escrito ainda">
                          {d.escritas.slice(0, 8).map((e, i) => (
                            <Linha key={i} rotulo={e.method}
                              cor={e.status && e.status >= 400 ? 'atencao' : 'neutro'}
                              meio={e.path} quando={e.created_at} mono />
                          ))}
                        </Bloco>
                      </div>
                    )
                  })()}
                </div>
                )
              })
            ) : (
              logins.length === 0 ? <Vazio texto="Nenhuma tentativa de login no período." />
              : logins.map(l => (
                <div key={l.id} className="px-3 py-1.5 border-b border-border/60 flex items-center gap-2 text-[11px]">
                  <span className={`shrink-0 w-2 h-2 rounded-full ${l.sucesso ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="shrink-0 w-32 truncate font-medium">{l.username || '—'}</span>
                  <span className="min-w-0 flex-1 truncate text-muted-foreground text-[10.5px]">
                    {l.sucesso ? 'entrou' : (l.motivo || 'falhou')}
                  </span>
                  <span className="shrink-0 w-20 truncate text-[10px] text-muted-foreground">{navegador(l.user_agent) || '—'}</span>
                  <span className={`shrink-0 w-40 truncate text-[10px] tabular-nums ${
                    suspeito(l.geo) ? 'text-amber-600 dark:text-amber-400 font-medium' : 'text-muted-foreground'
                  }`} title={l.geo?.operadora || ''}>
                    {l.ip || '—'}{procedencia(l.geo) && ` · ${procedencia(l.geo)}`}
                  </span>
                  <span className="shrink-0 text-[10px] text-muted-foreground tabular-nums">{quando(l.created_at)}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* ── Bloco 2: quem está dentro agora ── */}
        <div className="min-h-0 flex flex-col rounded-2xl glass-panel neu-bar overflow-hidden">
          <div className="shrink-0 px-3 py-2 border-b border-border flex items-center gap-2">
            <FiMonitor className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11.5px] font-semibold">Sessões abertas</span>
            <span className="ml-auto text-[10px] text-muted-foreground">derrubar encerra na hora</span>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto" style={semBarra}>
            {grupos.length === 0 ? <Vazio texto="Ninguém logado." /> : grupos.map(g => {
              const expandido = aberto === g.user_id
              const recente = g.sessoes[0]
              return (
                <div key={g.user_id} className="border-b border-border/60">
                  <div className="px-3 py-2 flex items-center gap-2">
                    <button onClick={() => setAberto(expandido ? null : g.user_id)}
                      className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[11.5px] font-medium truncate">{g.username || g.user_id}</span>
                        {!!g.is_admin && <span className="neu-tag rounded-full px-1.5 text-[9px] font-semibold text-primary">admin</span>}
                        {g.total > 1 && (
                          <span className="neu-tag rounded-full px-1.5 text-[9px] font-semibold text-muted-foreground tabular-nums">
                            {g.total} sessões
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">
                        {recente?.ip_address || <span className="italic">IP não gravado</span>}
                        {procedencia(recente?.geo) && (
                          <span className={suspeito(recente?.geo) ? 'text-amber-600 dark:text-amber-400 font-medium' : ''}>
                            {' · '}{procedencia(recente?.geo)}
                          </span>
                        )}
                        {navegador(recente?.user_agent) && ` · ${navegador(recente?.user_agent)}`}
                        {' · desde '}{quando(g.ultima)}
                      </div>
                    </button>
                    <button onClick={() => derruba({ url: `/api/seguranca/sessoes/usuario/${g.user_id}`, chave: g.user_id })}
                      disabled={derrubando === g.user_id}
                      title={g.total > 1 ? `Derrubar as ${g.total} sessões` : 'Derrubar esta sessão'}
                      className="shrink-0 p-1.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 disabled:opacity-50 transition-colors">
                      {derrubando === g.user_id ? <FiLoader className="w-3.5 h-3.5 animate-spin" /> : <FiSlash className="w-3.5 h-3.5" />}
                    </button>
                  </div>

                  {/* Abre só quando alguém pede: com 90 sessões de um token de
                      fundo, a lista aberta enterrava quem realmente entrou. */}
                  {expandido && g.sessoes.map(s => (
                    <div key={s.id} className="pl-6 pr-3 py-1 flex items-center gap-2 text-[10px] text-muted-foreground border-t border-border/40">
                      <span className="min-w-0 flex-1 truncate" title={s.user_agent || ''}>
                        {s.ip_address || 'sem IP'}
                        {procedencia(s.geo) && ` · ${procedencia(s.geo)}`}
                        {s.geo?.operadora && ` · ${s.geo.operadora}`}
                        {navegador(s.user_agent) && ` · ${navegador(s.user_agent)}`}
                      </span>
                      <span className="shrink-0 tabular-nums">{quando(s.created_at)}</span>
                      <button onClick={() => derruba({ url: `/api/seguranca/sessoes/${s.id}`, chave: s.id })}
                        disabled={derrubando === s.id} title="Derrubar só esta"
                        className="shrink-0 p-1 rounded hover:text-red-500 hover:bg-red-500/10 disabled:opacity-50">
                        {derrubando === s.id ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiSlash className="w-3 h-3" />}
                      </button>
                    </div>
                  ))}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

function AcaoBtn({ onClick, ocupado, icone: Icone, texto, perigo }: {
  onClick: () => void; ocupado: boolean; icone: IconType; texto: string; perigo?: boolean
}) {
  return (
    <button onClick={onClick} disabled={ocupado}
      className={`h-7 px-2.5 rounded-lg text-[10.5px] font-medium transition-colors flex items-center gap-1.5 disabled:opacity-50 ${
        perigo ? 'text-red-600 dark:text-red-400 hover:bg-red-500/10' : 'text-primary hover:bg-primary/10'
      } neu-seg-btn`}>
      {ocupado ? <FiLoader className="w-3 h-3 animate-spin" /> : <Icone className="w-3 h-3" />} {texto}
    </button>
  )
}

function Bloco({ titulo, icone: Icone, vazio, children }: {
  titulo: string; icone: IconType; vazio: string; children: React.ReactNode
}) {
  const vazioDeVerdade = !children || (Array.isArray(children) && children.length === 0)
  return (
    <div>
      <div className="text-[9.5px] font-semibold uppercase tracking-wider text-muted-foreground mb-0.5 flex items-center gap-1">
        <Icone className="w-2.5 h-2.5" /> {titulo}
      </div>
      {vazioDeVerdade
        ? <div className="text-[10px] text-muted-foreground italic">{vazio}</div>
        : children}
    </div>
  )
}

function Linha({ rotulo, meio, quando: q, cor, mono }: {
  rotulo: string; meio: string; quando: string; cor: 'ok' | 'atencao' | 'neutro'; mono?: boolean
}) {
  return (
    <div className="flex items-center gap-2 text-[10px] border-t border-border/40 py-0.5">
      <span className={`shrink-0 w-24 font-medium ${
        cor === 'ok' ? 'text-emerald-600 dark:text-emerald-400'
        : cor === 'atencao' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
      }`}>{rotulo}</span>
      <span className={`min-w-0 flex-1 truncate text-muted-foreground ${mono ? 'font-mono' : ''}`} title={meio}>{meio}</span>
      <span className="shrink-0 tabular-nums text-muted-foreground">{quando(q)}</span>
    </div>
  )
}

const Vazio = ({ texto }: { texto: string }) => (
  <div className="px-3 py-8 text-center text-[11.5px] text-muted-foreground">{texto}</div>
)
